import type { LibraryDocument } from '../../api/library'

/**
 * In what order the shelf shows what it holds.
 *
 * Four questions, and each of them is one somebody actually arrives with: what is it called, when
 * did it come, how big is it, what kind of thing is it. The direction is separate rather than baked
 * into the criterion — "Name" meaning only A-to-Z and "Newest" meaning only newest-first was two
 * fixed states wearing the costume of a choice, and the oldest document is as findable a thing as
 * the newest one.
 */
export const ORDERS = ['title', 'date', 'size', 'kind'] as const

export type Order = (typeof ORDERS)[number]

export const DEFAULT_ORDER: Order = 'title'

/** What each order is called where it is chosen. */
export const ORDER_NAMES: Readonly<Record<Order, string>> = {
  title: 'Title',
  date: 'Date',
  size: 'Size',
  kind: 'Kind',
}

/**
 * Which way round, said in the words of the criterion it applies to.
 *
 * "Ascending" and "Descending" are true of all four and mean nothing to anybody: for a date the
 * question is newest or oldest, for a size largest or smallest. The control says the one that
 * applies, which is why this is a lookup and not a pair of arrows.
 */
export const DIRECTION_NAMES: Readonly<
  Record<Order, { readonly up: string; readonly down: string }>
> = {
  title: { up: 'A to Z', down: 'Z to A' },
  date: { up: 'Oldest first', down: 'Newest first' },
  size: { up: 'Smallest first', down: 'Largest first' },
  kind: { up: 'A to Z', down: 'Z to A' },
}

/**
 * Which way a criterion runs when it is first chosen.
 *
 * A name starts at A, and a date starts at the newest — because "sort by date" is nearly always
 * asked by somebody looking for what just arrived. Same for size: the big one is the one being
 * hunted when the question comes up at all.
 */
export function naturalDescent(order: Order): boolean {
  return order === 'date' || order === 'size'
}

export interface Sorting {
  readonly by: Order
  readonly descending: boolean
}

export const DEFAULT_SORTING: Sorting = {
  by: DEFAULT_ORDER,
  descending: naturalDescent(DEFAULT_ORDER),
}

/**
 * The documents in the order asked for.
 *
 * Sorted in the browser over what was fetched anyway, for the reason the subject filter is: a folder
 * holds tens of documents and not thousands, and an order that needed a request would flicker.
 *
 * **The title decides every tie**, whatever the criterion. Two documents of the same kind, or of the
 * same size, in an order that depends on which of them the service happened to mention first is an
 * order that rearranges itself for no reason anybody can see.
 */
export function sorted(documents: readonly LibraryDocument[], how: Sorting): LibraryDocument[] {
  const direction = how.descending ? -1 : 1
  return [...documents].sort((one, other) => {
    const difference = compare(one, other, how.by)
    return difference !== 0 ? difference * direction : byTitle(one, other)
  })
}

function compare(one: LibraryDocument, other: LibraryDocument, by: Order): number {
  switch (by) {
    case 'date':
      return one.takenOverAt.localeCompare(other.takenOverAt)
    case 'size':
      return one.sizeInBytes - other.sizeInBytes
    case 'kind':
      // A document whose kind is unknown sorts as the empty string, which puts it first ascending.
      // That is honest: it is not a kind, and inventing a word for it would file it under a letter.
      return one.kind.localeCompare(other.kind)
    default:
      return byTitle(one, other)
  }
}

/** Titles are compared the way somebody reads them, so `Übung` lands where German expects it. */
function byTitle(one: LibraryDocument, other: LibraryDocument): number {
  return one.title.localeCompare(other.title)
}
